import { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Appbar, Button, Snackbar, Surface, Switch, Text, useTheme } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { ConsentSettings, getStoredConsent, saveConsent } from '@/services/consent';
import { applyAnalyticsConsent } from '@/src/lib/analytics';
import { applySentryConsent } from '@/src/lib/sentryInit';

export default function PrivacySettings() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(false);
  // Crash reports are opt-out (decisão 2026-07-02): undecided counts as on.
  const [crashReports, setCrashReports] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    getStoredConsent().then((stored) => {
      if (!mounted) return;
      setAnalytics(stored?.analytics ?? false);
      setCrashReports(stored?.crashReports ?? true);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const consent: ConsentSettings = await saveConsent({ analytics, crashReports });
      await Promise.all([
        applyAnalyticsConsent(consent),
        applySentryConsent(consent),
      ]);
      setSnackbarVisible(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Surface style={styles.container}>
      <Appbar.Header style={{ backgroundColor: theme.colors.surfaceVariant }}>
        <Appbar.BackAction onPress={() => router.back()} />
        <Appbar.Content title={t('privacy.settingsItemTitle')} />
      </Appbar.Header>

      <ScrollView contentContainerStyle={styles.content}>
        <Surface elevation={3} style={styles.section}>
          <Text variant="titleMedium" style={styles.title}>{t('privacy.sectionTitle')}</Text>
          <Text variant="bodyMedium" style={styles.copy}>
            {t('privacy.sectionBody')}
          </Text>

          <View style={styles.row}>
            <View style={styles.rowText}>
              <Text variant="bodyLarge">{t('privacy.analyticsLabel')}</Text>
              <Text variant="bodySmall">{t('privacy.analyticsDesc')}</Text>
            </View>
            <Switch value={analytics} onValueChange={setAnalytics} disabled={loading || saving} />
          </View>

          <View style={styles.row}>
            <View style={styles.rowText}>
              <Text variant="bodyLarge">{t('privacy.crashLabel')}</Text>
              <Text variant="bodySmall">{t('privacy.crashDesc')}</Text>
            </View>
            <Switch value={crashReports} onValueChange={setCrashReports} disabled={loading || saving} />
          </View>
        </Surface>

        <Button mode="contained" onPress={save} loading={saving} disabled={loading || saving}>
          {t('privacy.savePreferences')}
        </Button>
      </ScrollView>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setSnackbarVisible(false) }}
      >
        {t('privacy.saved')}
      </Snackbar>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  section: {
    padding: 16,
    borderRadius: 16,
    marginBottom: 24,
  },
  title: {
    marginBottom: 8,
    fontWeight: '700',
  },
  copy: {
    marginBottom: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
  },
  rowText: {
    flex: 1,
  },
});
